// src/utils/camera.js
// Profile photo picker using @capacitor/camera on native iOS.
//
// On native: opens the native action sheet (library or camera), resizes to
//   400x400, uploads to Supabase Storage, and returns the public URL.
// On web: falls back to a standard <input type="file"> pick (called externally).
//
// NSPhotoLibraryUsageDescription must be set in Info.plist — see
//   docs/ios/Info.plist-additions.xml for the exact entry to add in Xcode.

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { supabase } from '../supabaseClient.js';

/** Returns true when the Capacitor Camera plugin should be used. */
export function hasCameraPlugin() {
  return Capacitor.isNativePlatform();
}

/**
 * Convert a base64 data URL to a Blob.
 */
function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * Upload a blob to Supabase Storage (avatars bucket) and return the public URL.
 */
async function uploadToStorage(blob, userId) {
  const ext = blob.type === 'image/png' ? 'png' : 'jpg';
  const path = `${userId}/avatar.${ext}`;
  const { error } = await supabase.storage
    .from('avatars')
    .upload(path, blob, { upsert: true, contentType: blob.type });
  if (error) throw error;
  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  // Bust cache so the new image is fetched immediately
  return `${data.publicUrl}?t=${Date.now()}`;
}

/**
 * Open the native photo picker / camera, upload to Supabase Storage, and
 * return the public URL. Falls back to returning the data URL if upload fails.
 *
 * @returns {Promise<string>}  public URL (or data URL as fallback)
 * @throws  If the user cancels or permission is denied
 */
export async function pickProfilePhoto() {
  const photo = await Camera.getPhoto({
    quality: 80,
    allowEditing: true,
    resultType: CameraResultType.DataUrl,
    source: CameraSource.Prompt,
    width: 400,
    height: 400,
    correctOrientation: true,
  });

  if (!photo.dataUrl) throw new Error('no_photo_returned');

  // Try to upload to storage so we store a URL instead of a raw base64 blob
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id) {
      const blob = dataUrlToBlob(photo.dataUrl);
      return await uploadToStorage(blob, user.id);
    }
  } catch {
    // Upload failed — fall back to data URL so the user still sees their photo
    // locally, but warn that it won't sync to others
    console.warn('[camera] Storage upload failed, falling back to data URL');
  }

  return photo.dataUrl;
}

/**
 * Upload a File object (from a web <input type="file">) to Supabase Storage
 * and return the public URL. Used by the web profile photo picker.
 *
 * @param {File} file
 * @param {string} userId
 * @returns {Promise<string>} public URL
 */
export async function uploadFileToStorage(file, userId) {
  return await uploadToStorage(file, userId);
}
